<template>
  <EquipmentBase :section-id="ScenarioDetailSectionKey.FLUID_PROPERTIES">
    <EquipmentHeader>
      <template #title>
        <div class="flex justify-content-between align-items-center flex-wrap">
          Fluid Properties at Standard Conditions
          <CalculatorToggle
            :section-ids="[EquipmentCalculatorSectionKey.FLUID_PROPERTIES]"
          />
        </div>
      </template>
    </EquipmentHeader>

    <div class="field-grid">
      <ConverterInput
        v-if="!isAmineOrWater"
        :label="isHpsCondensate ? 'Condensate Gravity' : 'Oil Gravity'"
        :base-unit="DensityUnits.API"
        :model="
          DensityUnits.options([
            DensityUnits.KilogramsPerCubicMeter,
            DensityUnits.GasPoundsPerCubicFoot
          ])
        "
        mode="write"
        :model-value="wellProperties.oil_gravity_api"
        @update:model-value="
          wellPropertiesForm.update({ oil_gravity_api: $event })
        "
      />
      <ConverterInput
        v-if="!isHpsOil && !isHpsCondensate"
        :class="{ 'liquid-gravity': isAmineOrWater }"
        :label="
          wellProperties.hps_liquid_type === Hps_Liquid_Type_Enum.Amine
            ? 'Amine Gravity'
            : 'Water Gravity'
        "
        :base-unit="DensityUnits.SpecificGravity"
        :model="
          DensityUnits.options([
            DensityUnits.API,
            DensityUnits.GasPoundsPerCubicFoot
          ])
        "
        :decimal-places="3"
        mode="write"
        :model-value="wellProperties.water_specific_gravity"
        @update:model-value="
          wellPropertiesForm.update({ water_specific_gravity: $event })
        "
      />
      <ConverterInput
        v-if="isEspOnShore"
        label="Bubble Point Pressure Override"
        :disabled="shouldDisableBubblePointPressure"
        :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
        :model="PressureUnits.options()"
        :nullable="true"
        mode="write"
        :model-value="wellProperties.bubble_point_pressure_psia"
        @update:model-value="
          wellPropertiesForm.update({ bubble_point_pressure_psia: $event })
        "
      />
      <ConverterInput
        v-if="isEspOnShore"
        label="Bubble Point Pressure"
        :model-value="selectedBubblePointPressurePSIA"
        :base-unit="PressureUnits.PoundsPerSquareInchAbsolute"
        :model="PressureUnits.options()"
        mode="convert"
      />
      <ConverterInput
        label="Fluid Viscosity Override"
        :base-unit="ViscosityUnits.Centipoise"
        :model="ViscosityUnits.options()"
        :nullable="true"
        mode="write"
        :model-value="wellProperties.fluid_viscosity_override_cp"
        @update:model-value="
          wellPropertiesForm.update({ fluid_viscosity_override_cp: $event })
        "
      />
      <ConverterInput
        label="Fluid Viscosity"
        :model-value="selectedFluidViscosityCP"
        :base-unit="ViscosityUnits.Centipoise"
        :model="ViscosityUnits.options()"
        mode="convert"
      />
    </div>

    <Divider>Temperature</Divider>

    <div class="grid">
      <ConverterInput
        v-if="isEspOnShore"
        class="col-12 md:col-6 lg:col-3"
        label="Temperature at Datum"
        :base-unit="TemperatureUnits.Fahrenheit"
        :model="TemperatureUnits.options()"
        mode="write"
        :model-value="wellProperties.bottomhole_temperature_fahrenheit"
        @update:model-value="
          wellPropertiesForm.update({
            bottomhole_temperature_fahrenheit: $event
          })
        "
      />
      <ConverterInput
        v-else
        :class="{ 'liquid-gravity': isAmineOrWater }"
        label="HPS Inlet Temperature"
        :base-unit="TemperatureUnits.Fahrenheit"
        :model="TemperatureUnits.options()"
        mode="write"
        :model-value="wellProperties.hps_pump_inlet_temperature_fahrenheit"
        @update:model-value="
          wellPropertiesForm.update({
            hps_pump_inlet_temperature_fahrenheit: $event
          })
        "
      />
      <ConverterInput
        v-if="isEspOnShore"
        class="col-12 md:col-6 lg:col-3"
        label="Surface Temperature"
        :base-unit="TemperatureUnits.Fahrenheit"
        :model="TemperatureUnits.options()"
        mode="write"
        :model-value="wellProperties.surface_temperature_fahrenheit"
        @update:model-value="
          wellPropertiesForm.update({ surface_temperature_fahrenheit: $event })
        "
      />
    </div>
  </EquipmentBase>
</template>

<script setup lang="ts">
import { computed } from "vue";
import EquipmentBase from "@/components/scenarios/EquipmentBase.vue";
import EquipmentHeader from "@/components/scenarios/EquipmentHeader.vue";
import ConverterInput from "@/components/ConverterInput.vue";
import CalculatorToggle from "@/components/scenarios/calculator_sections/CalculatorToggle.vue";

import { PressureUnits } from "@/libs/units/pressure";
import { TemperatureUnits } from "@/libs/units/temperature";
import { DensityUnits } from "@/libs/units/density";
import { ViscosityUnits } from "@/libs/units/viscosity";
import { injectStrict } from "@/utils/utils";
import {
  wellPropertiesFormKey,
  scenarioCalculationsKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { ScenarioDetailSectionKey } from "@/composables/scenarioCalculations/useEquipmentCalculatorSections";
import { EquipmentCalculatorSectionKey } from "@/composables/scenarioCalculations/calculationTypes";
import type { RegressionCoefficientsMap } from "@/composables/scenarioCalculations/linearizationAlgorithm";

import { Hps_Liquid_Type_Enum } from "gql/api";

const props = withDefaults(defineProps<{ isEspOnShore?: boolean }>(), {
  isEspOnShore: true
});

defineEmits(["update:well-properties", "hover"]);

const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
const wellProperties = computed(() => wellPropertiesForm.data.value);

const isAmineOrWater = computed(() => {
  const type = wellProperties.value?.hps_liquid_type;
  return (
    type === Hps_Liquid_Type_Enum.Amine || type === Hps_Liquid_Type_Enum.Water
  );
});

const isHpsOil = computed(() => {
  return (
    !props.isEspOnShore &&
    wellProperties.value?.hps_liquid_type === Hps_Liquid_Type_Enum.Oil
  );
});

const isHpsCondensate = computed(
  () =>
    !props.isEspOnShore &&
    wellProperties.value?.hps_liquid_type === Hps_Liquid_Type_Enum.Condensate
);

const { selectedBubblePointPressurePSIA, selectedFluidViscosityCP } =
  injectStrict(scenarioCalculationsKey);

const isEspOnShore = computed(() => props.isEspOnShore);

// Disable Bubble Point Pressure Override if any of the custom fluid properties
// have a bubble point pressure value
const shouldDisableBubblePointPressure = computed(() => {
  return wellProperties.value.custom_fluid_properties?.some(
    (row: RegressionCoefficientsMap) => row.bubblePointPressurePSIA !== null
  );
});
</script>
<style lang="scss" scoped>
.liquid-gravity {
  max-width: 318px;
}
</style>
